import { ArrowLeftCircle, Check, Download, Save } from 'lucide-react';
import { isDesktop } from '@/lib/fileSystem';
import PageCanvas from './PageCanvas';

/**
 * ResultsScreen — Shared results view for Extract and Split tabs.
 *
 * Shows a list of generated PDFs with:
 *   - Thumbnail (first page of each result)
 *   - Editable filename
 *   - Subtitle (page count/range + file size)
 *   - Individual download button
 *   - Download All as ZIP
 *   - Back button to return to the previous view
 */
export default function ResultsScreen({
  results,
  pdfDoc,
  onUpdateName,
  onDownloadOne,
  onDownloadAll,
  onBack,
  backLabel,
}) {
  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 transition-colors"
        >
          <ArrowLeftCircle size={18} />
          {backLabel || 'Back'}
        </button>
        <div className="flex-1" />
        <div className="flex items-center gap-2 text-sm text-green-700 bg-green-50 border border-green-200 px-3 py-1.5 rounded-full">
          <Check size={14} />
          {results.length} PDF{results.length !== 1 ? 's' : ''} created
        </div>
      </div>

      <div className="space-y-2 mb-6">
        {results.map((result, index) => (
          <div
            key={index}
            className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-4"
          >
            <div className="w-10 h-14 rounded border border-gray-200 overflow-hidden shrink-0 bg-white">
              <PageCanvas
                pdfDoc={pdfDoc}
                pageNumber={result.thumbnailPage}
                scale={0.1}
              />
            </div>

            <div className="flex-1 min-w-0">
              <input
                type="text"
                value={result.name}
                onChange={(e) => onUpdateName(index, e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && e.target.blur()}
                className="text-sm font-medium text-gray-900 w-full bg-transparent border-b border-transparent hover:border-gray-300 focus:border-brand-blue focus:outline-none px-1 py-0.5 -ml-1 rounded"
              />
              <p className="text-xs text-gray-500 mt-0.5">{result.subtitle}</p>
            </div>

            <button
              onClick={() => onDownloadOne(result)}
              className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 hover:text-brand-blue transition-colors"
              title={isDesktop() ? 'Save' : 'Download'}
            >
              {isDesktop() ? <Save size={18} /> : <Download size={18} />}
            </button>
          </div>
        ))}
      </div>

      <button
        onClick={onDownloadAll}
        className="w-full flex items-center justify-center gap-2 px-5 py-3 bg-brand-blue text-white rounded-xl font-medium hover:bg-brand-dark transition-colors"
      >
        {isDesktop() ? <Save size={18} /> : <Download size={18} />}
        {isDesktop() ? 'Save' : 'Download'} All as ZIP
      </button>
    </div>
  );
}
